import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthenticationService } from '@core/services/authentication.service';
import { CompanyService } from '@core/services/company.service';
import { NotificationService } from '@core/services/notifications.service';
import { EDataMutation } from '@core/services/posthog.service';
import { environment } from '@env/environment';
import {
  ICreateQuestion,
  IFindQuestion,
  IPatchQuestion,
  IQuestionResult,
} from '@meeting/models/meeting-question.model';
import { ITemplateResult } from '@meeting/models/meeting-template.model';
import { sortMeetingQuestionsByTextLocale } from '@meeting/utils/meeting-questions.utils';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class MeetingQuestionsService {
  private _ENDPOINT_URL = `${environment.API_URL}/meeting/questions`;

  private questions$$: BehaviorSubject<IQuestionResult[] | undefined> =
    new BehaviorSubject<IQuestionResult[] | undefined>([]);
  question$ = this.questions$$.asObservable();

  private _selectedQuestionId$$: BehaviorSubject<string | undefined> =
    new BehaviorSubject<string | undefined>(undefined);

  selectedQuestionId$ = this._selectedQuestionId$$.asObservable();
  isSelectedQuestionSystem = new BehaviorSubject<boolean>(true);

  private _loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );
  loading$ = this._loading$$.asObservable();

  public emitQuestionEvent = new Subject<{
    mutation: EDataMutation;
    question: IQuestionResult;
  }>();

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _authService: AuthenticationService,
    private readonly _companyService: CompanyService,
    private readonly _notificationService: NotificationService,
  ) {
    this._authService.currentUser$
      .pipe(distinctUntilChanged())
      .subscribe((user) => {
        user && this._initStore();
      });
  }

  private _initStore() {
    this.refreshStore$().subscribe({
      error: (err) =>
        console.error(`Erreur lors de l'initialisation du store`, err),
    });
  }

  refreshStore$(): Observable<IQuestionResult[]> {
    return this._authService.currentUser$.pipe(
      take(1),
      distinctUntilChanged((a, b) => a?.id === b?.id),
      switchMap((user) => {
        if (!user) {
          return of([]);
        }
        this._loading$$.next(true);
        return this.getAll$().pipe(
          take(1),
          tap((questions) => {
            this.questions$$.next(questions);
            this._loading$$.next(false);
          }),
        );
      }),
    );
  }

  getAll$(): Observable<IQuestionResult[]> {
    return this._httpClient.get<IQuestionResult[]>(this._ENDPOINT_URL);
  }

  getOne$(questionId: string): Observable<IQuestionResult> {
    return this._httpClient.get<IQuestionResult>(
      this._ENDPOINT_URL + '/' + questionId,
    );
  }

  getQuestionsFromCategory$(
    questionToFind: IFindQuestion,
  ): Observable<IQuestionResult[]> {
    return this.question$.pipe(
      map((questions) =>
        questions.filter(
          (question) => question.categoryId === questionToFind.categoryId,
        ),
      ),
    );
  }

  getSortedQuestionsByCategory$(categoryId: string) {
    return combineLatest([
      this.getQuestionsFromCategory$({ categoryId }),
      this._authService.currentUser$.pipe(map((user) => user.locale)),
      this._companyService.currentCompany$.pipe(
        map((company) => company?.defaultLocale),
      ),
    ]).pipe(
      map(([questions, userLocale, companyLocale]) => {
        const sortedQuestions = questions.sort((a, b) => {
          if (a.textReviewee[userLocale]) {
            return sortMeetingQuestionsByTextLocale(userLocale, a, b);
          } else if (a.textReviewee[companyLocale]) {
            return sortMeetingQuestionsByTextLocale(companyLocale, a, b);
          }
          return;
        });

        return sortedQuestions;
      }),
    );
  }

  getFilteredAndSortedQuestionsByCategory$(
    categoryId: string,
    searchTerm: string,
    filter: {
      isReferentielQuestionInclude: boolean;
      isCustomQuestionInclude: boolean;
      isNoTemplateInclude: boolean;
      templateSelectList: Array<{
        isChecked: boolean;
        templateId: string;
        title: string;
      }>;
    },
    templates: ITemplateResult[],
  ) {
    return combineLatest([
      this.getSortedQuestionsByCategory$(categoryId),
      this._authService.currentUser$.pipe(map((user) => user.locale)),
      this._companyService.currentCompany$.pipe(
        map((company) => company?.defaultLocale),
      ),
    ]).pipe(
      map(([questions, userLocale, companyLocale]) => {
        const filteredQuestions = questions.filter((q) => {
          const questionTextLocalized =
            q.textReviewee[userLocale] || q.textReviewee[companyLocale] || '';
          const hasText =
            !searchTerm ||
            questionTextLocalized
              .toLowerCase()
              .includes(searchTerm.toLowerCase());

          if (!hasText) {
            return false;
          }

          const isNoSystemFilter =
            !filter.isReferentielQuestionInclude &&
            !filter.isCustomQuestionInclude;

          if (!isNoSystemFilter) {
            const isFilterReferentielPassed =
              q.isSystem && filter.isReferentielQuestionInclude;
            const isFilterCustomPassed =
              !q.isSystem && filter.isCustomQuestionInclude;

            if (!isFilterReferentielPassed && !isFilterCustomPassed)
              return false;
          }

          const isTemplateFilterActive = !!filter.templateSelectList?.filter(
            (template) => {
              return template.isChecked;
            },
          )?.length;

          if (isTemplateFilterActive || filter.isNoTemplateInclude) {
            const isFilterTemplatePassed =
              isTemplateFilterActive &&
              templates.find((template) => {
                const isTemplateInclude = !!filter.templateSelectList.find(
                  (selectedTemplate) => {
                    return (
                      selectedTemplate.isChecked &&
                      template.id === selectedTemplate.templateId
                    );
                  },
                );

                if (!isTemplateInclude) {
                  return false;
                }

                return !!template.questions.find((question) => {
                  return question.questionId === q.id;
                });
              });

            const isNoTemplateLinkedFilterPassed =
              filter.isNoTemplateInclude &&
              !templates.find((template) => {
                return !!template.questions.find((question) => {
                  return question.questionId === q.id;
                });
              });

            if (!isFilterTemplatePassed && !isNoTemplateLinkedFilterPassed) {
              return false;
            }
          }
          return true;
        });

        return filteredQuestions;
      }),
    );
  }

  getQuestionById$(questionId: string) {
    return this.question$.pipe(
      map((questions) =>
        questions.find((question) => question.id === questionId),
      ),
    );
  }

  getQuestionByIds$(questionIds: string[]) {
    return this.question$.pipe(
      map((questions) =>
        questions.filter((question) => questionIds.includes(question.id)),
      ),
    );
  }

  create$(question: ICreateQuestion): Observable<IQuestionResult> {
    return this._httpClient
      .post<IQuestionResult>(this._ENDPOINT_URL, question)
      .pipe(
        tap((questionResult) => {
          this._notificationService.showSuccess('MEETING.ADD_QUESTION_SUCCESS');
          this.emitQuestionEvent.next({
            mutation: EDataMutation.CREATED,
            question: questionResult,
          });
        }),
        switchMap((createdQuestion) =>
          this.refreshStore$().pipe(map(() => createdQuestion)),
        ),
        catchError((_err) => {
          this._notificationService.showError('ALERTS.GENERIC_ERROR');
          return of(undefined);
        }),
      );
  }

  patch$(
    questionId: string,
    questionToPatch: IPatchQuestion,
  ): Observable<IQuestionResult> {
    return this._httpClient
      .patch<IQuestionResult>(
        `${this._ENDPOINT_URL}/${questionId}`,
        questionToPatch,
      )
      .pipe(
        tap((question) => {
          this._notificationService.showSuccess(
            'MEETING.UPDATE_QUESTION_SUCCESS',
          );
          this.emitQuestionEvent.next({
            mutation: EDataMutation.MODIFIED,
            question,
          });
        }),
        switchMap((updatedQuestion) =>
          this.refreshStore$().pipe(map(() => updatedQuestion)),
        ),
        catchError((_err) => {
          this._notificationService.showError('ALERTS.GENERIC_ERROR');
          return of(undefined);
        }),
      );
  }

  delete$(id: string): Observable<IQuestionResult> {
    return this._httpClient
      .delete<IQuestionResult>(`${this._ENDPOINT_URL}/${id}`)
      .pipe(
        tap((question) => {
          this._notificationService.showSuccess(
            'MEETING.UPDATE_QUESTION_SUCCESS',
          );
          this.emitQuestionEvent.next({
            mutation: EDataMutation.DELETED,
            question,
          });
        }),
        switchMap((deletedQuestion) =>
          this.refreshStore$().pipe(map(() => deletedQuestion)),
        ),
        catchError((_err) => {
          this._notificationService.showError('ALERTS.GENERIC_ERROR');
          return of(undefined);
        }),
      );
  }

  setSelectedQuestionById(questionId: string, isSystem: boolean) {
    this._selectedQuestionId$$.next(questionId);
    this.isSelectedQuestionSystem.next(isSystem);
  }

  resetSelectedQuestion() {
    this._selectedQuestionId$$.next(undefined);
    this.isSelectedQuestionSystem.next(true);
  }
}
